import { getRefreshToken } from 'axios-jwt'

/**
 * Reads the `exp` claim out of a JWT without verifying it.
 *
 * This is a hint, never a decision about trust: the server validates the signature on
 * every request and remains the only authority on whether a token is good. All this is
 * used for is deciding whether to render an app we already know every request will be
 * rejected on.
 */
const readExpiry = (token: string): number | undefined => {
  try {
    const payload = token.split('.')[1]
    if (!payload) return undefined
    const claims = JSON.parse(atob(payload.replace(/-/g, '+').replace(/_/g, '/')))
    return typeof claims.exp === 'number' ? claims.exp : undefined
  } catch {
    return undefined
  }
}

/**
 * Whether a refresh token is stored and has not expired.
 *
 * `isLoggedIn()` from axios-jwt only answers "is a refresh token string present", so it
 * stays true forever once one has been stored. The persisted easy-peasy store has the
 * same problem from the other side: it is written to IndexedDB and never expires. Put
 * together, a user coming back after their 30-day refresh token lapsed was shown a
 * signed-in application, and then signed out mid-page by the 401 interceptor as soon as
 * the first request came back -- which reads, to them, as being kicked out at random.
 *
 * A token whose payload will not decode is left to the server rather than treated as
 * expired -- wrongly signing someone out would be a worse bug than the one this prevents.
 * A token *store* that will not parse is different: it holds nothing usable, so that
 * answers false.
 */
export const hasLiveRefreshToken = (): boolean => {
  let token: string | undefined
  try {
    token = getRefreshToken()
  } catch {
    // getRefreshToken() re-throws when the stored JSON will not parse, and this runs
    // inside PrivateRoute's render -- where a throw reaches the app-wide ErrorBoundary
    // and takes the whole page down. A token store we cannot read holds no usable token,
    // so the honest answer is "signed out".
    return false
  }
  if (!token) return false

  const expiry = readExpiry(token)
  if (expiry === undefined) return true

  return expiry * 1000 > Date.now()
}
